import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { catchError, forkJoin, of } from 'rxjs';
import { Permission } from '../../../../core/enums/permission.enum';
import { EvaluationCategoryGroupSalaryRange } from '../../../../core/models/evaluation-category-group-salary-range';
import { EvaluationCategoryGroupSalaryRangeService } from '../../../../core/services/evaluation-category-group-salary-range.service';
import { PracticeScopeService } from '../../../../core/services/practice-scope.service';
import { CategoryMappingService } from '../../../../core/services/category-mapping.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { EmPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { EvaluationSalaryRangeFormComponent } from './evaluation-salary-range-form/evaluation-salary-range-form.component';

type SalaryField = 'lowSalary' | 'mediumSalary' | 'highSalary' | 'exceedSalary';

interface SalaryColumn {
  field: SalaryField;
  titleKey: string;
}

/** How a practice and a category label are compared everywhere in this page. */
function normalise(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

/**
 * The Salary Range configuration, per practice. The page behaves like the
 * Remuneration & Benefits tab: pick a practice, edit the four reference values
 * directly in the table and save the whole grid with one button; each row keeps
 * its own delete action.
 */
@Component({
  selector: 'em-evaluation-salary-ranges',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    DropdownModule,
    InputNumberModule,
    TableModule,
    ToastModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './evaluation-salary-ranges.component.html',
  styleUrl: './evaluation-salary-ranges.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class EvaluationSalaryRangesComponent implements OnInit {
  /** Working copy edited in the table, filtered to the selected practice. */
  ranges: EvaluationCategoryGroupSalaryRange[] = [];
  /** Saved rows of the selected practice: the reference for the dirty check. */
  private savedRows: EvaluationCategoryGroupSalaryRange[] = [];
  /** Loaded rows of every practice. */
  private allRanges: EvaluationCategoryGroupSalaryRange[] = [];
  /**
   * Category short labels of each practice, both normalised, built from the
   * category mappings. It is what tells a category apart from another practice'
   * one, so filtering by a practice shows - and offers - only its own categories,
   * exactly like the Remuneration & Benefits grid does.
   */
  categoryLabelsByPractice = new Map<string, Set<string>>();

  practices: string[] = [];
  selectedPractice: string | null = null;
  loading = false;
  saving = false;
  translations: any = {};

  readonly canWrite: boolean;
  /** True for a Manager / Manager People: they work on their own practice only. */
  readonly isPracticeScoped: boolean;
  Permission = Permission;

  readonly columns: SalaryColumn[] = [
    { field: 'lowSalary', titleKey: 'layout.configurations.evaluation.salaryRanges.columns.low' },
    { field: 'mediumSalary', titleKey: 'layout.configurations.evaluation.salaryRanges.columns.medium' },
    { field: 'highSalary', titleKey: 'layout.configurations.evaluation.salaryRanges.columns.high' },
    { field: 'exceedSalary', titleKey: 'layout.configurations.evaluation.salaryRanges.columns.exceed' },
  ];

  private loadedPractice: string | null = null;

  constructor(
    private evaluationCategoryGroupSalaryRangeService: EvaluationCategoryGroupSalaryRangeService,
    private categoryMappingService: CategoryMappingService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private practiceScope: PracticeScopeService,
  ) {
    this.canWrite = this.permissionService.hasPermission(Permission.CONFIG_PERFORMANCE);
    this.isPracticeScoped = this.practiceScope.isPracticeScopedUser();
  }

  /**
   * Categories of the selected practice that do not have a range yet. Only the
   * categories of that practice are ever offered, so a range can never be filed
   * under a practice it does not belong to.
   */
  get availableCategories(): string[] {
    return this.availableFor(this.selectedPractice);
  }

  get hasAvailableCategories(): boolean {
    return this.availableCategories.length > 0;
  }

  /** The Add dialog can assign a range to any practice, so it stays offered while at least one practice still has a free category. */
  get canAddAnywhere(): boolean {
    return this.practices.some((practice) => this.availableFor(practice).length > 0);
  }

  private availableFor(practice: string | null): string[] {
    if (practice === null) {
      return [];
    }
    const configured = new Set(
      this.savedRangesFor(practice)
        .map((range) => normalise(range.categoryShortLabel))
        .filter((label) => !!label),
    );
    return this.labelsFor(practice).filter((label) => !configured.has(label));
  }

  /** The normalised short labels of the categories that belong to a practice. */
  private labelsFor(practice: string | null): string[] {
    if (practice === null) {
      return [];
    }
    const labels = this.categoryLabelsByPractice.get(normalise(practice));
    return labels ? [...labels].sort((a, b) => a.localeCompare(b)) : [];
  }

  /** True once the practices were loaded, so "nothing selected" is not shown after a failed load. */
  get practicesLoaded(): boolean {
    return this.practices.length > 0 || this.selectedPractice !== null;
  }

  /** Values are reference amounts, so an empty cell simply means "not filled in yet" rather than zero. */
  formatAmount(value: number | null): string {
    return value === null || value === undefined ? '-' : (this.numberPipe.transform(value, '1.0-2') ?? '-');
  }

  /**
   * Selects the whole amount as soon as a cell takes focus, so typing overwrites
   * the value instead of being appended to it: an update needs no clearing first.
   */
  selectAllOnFocus(event: Event): void {
    (event.target as HTMLInputElement | null)?.select();
  }

  get isDirty(): boolean {
    return JSON.stringify(this.values(this.ranges)) !== JSON.stringify(this.values(this.savedRows));
  }

  /** Save is possible only after something changed and while nothing is loading or saving. */
  get canSave(): boolean {
    return this.canWrite && this.selectedPractice !== null && !this.loading && !this.saving && this.isDirty;
  }

  /** Cells can be edited only by users with the configuration permission and while idle. */
  get canEdit(): boolean {
    return this.canWrite && !this.loading && !this.saving;
  }

  ngOnInit(): void {
    this.loadTranslations();
    this.getAll();
  }

  private loadTranslations(): void {
    this.translate
      .get([
        'layout.configurations.evaluation.actions',
        'layout.configurations.evaluation.salaryRanges.title',
        'layout.configurations.evaluation.salaryRanges.practice',
        'layout.configurations.evaluation.salaryRanges.addRange',
        'layout.configurations.evaluation.salaryRanges.emptyMessage',
        'layout.configurations.evaluation.salaryRanges.save',
        'layout.configurations.evaluation.salaryRanges.cancel',
        'layout.configurations.evaluation.salaryRanges.saved',
        'layout.configurations.evaluation.salaryRanges.successMessage.title',
        'layout.configurations.evaluation.salaryRanges.addRangeDialog.title',
        'layout.configurations.evaluation.salaryRanges.addRangeDialog.successMessage.title',
        'layout.configurations.evaluation.salaryRanges.addRangeDialog.successMessage.detail',
        'layout.configurations.evaluation.salaryRanges.deleteRange.title',
        'layout.configurations.evaluation.salaryRanges.deleteRange.detail',
        'layout.configurations.evaluation.salaryRanges.deleteRange.delete',
        'layout.configurations.evaluation.salaryRanges.deleteRange.cancel',
        'layout.configurations.evaluation.salaryRanges.deleteRange.cancelDeletion.title',
        'layout.configurations.evaluation.salaryRanges.deleteRange.cancelDeletion.detail',
        'layout.configurations.evaluation.salaryRanges.deleteRange.successMessage.title',
        'layout.configurations.evaluation.salaryRanges.deleteRange.successMessage.detail',
        'layout.configurations.evaluation.salaryRanges.errorMessage.title',
        'layout.configurations.evaluation.salaryRanges.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAll(): void {
    this.loading = true;
    forkJoin({
      ranges: this.evaluationCategoryGroupSalaryRangeService.getAll(),
      practices: this.evaluationCategoryGroupSalaryRangeService.getPractices(),
      categories: this.categoryMappingService.getAll().pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ ranges, practices, categories }) => {
        this.allRanges = ranges ?? [];
        this.practices = this.practiceScope.restrict(practices ?? [], (practice) => practice);
        this.groupLabelsByPractice(categories);
        if (this.selectedPractice === null || (this.practices.length > 0 && !this.practices.includes(this.selectedPractice))) {
          this.selectedPractice = this.practices[0] ?? null;
        }
        this.applySelectedPractice();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.log('Error loading the evaluation salary ranges: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.title'],
          detail: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.detail'],
        });
      },
    });
  }

  onPracticeChange(): void {
    if (!this.isDirty) {
      this.applySelectedPractice();
      return;
    }
    this.confirmationService.confirm({
      key: 'salaryRanges',
      header: this.translate.instant('worklog.unsavedChangesHeader'),
      message: this.translate.instant('worklog.unsavedChangesMessage'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('worklog.leave'),
      rejectLabel: this.translate.instant('worklog.stay'),
      accept: () => this.applySelectedPractice(),
      reject: () => {
        this.selectedPractice = this.loadedPractice;
      },
    });
  }

  private applySelectedPractice(): void {
    this.savedRows = this.savedRangesFor(this.selectedPractice);
    this.ranges = this.copyRanges(this.savedRows);
    this.loadedPractice = this.selectedPractice;
  }

  private allRangesFor(practice: string | null): EvaluationCategoryGroupSalaryRange[] {
    if (practice === null) {
      return [];
    }
    return this.allRanges.filter((range) => normalise(range.practice) === normalise(practice));
  }

  /**
   * Saved rows of a practice, keeping only the categories that belong to it: a
   * range filed under the wrong practice used to show up as soon as that practice
   * was selected. A label no category mapping knows about stays, because it
   * cannot be attributed to another practice, and the whole list is kept when the
   * mappings of the practice are unknown.
   */
  private savedRangesFor(practice: string | null): EvaluationCategoryGroupSalaryRange[] {
    const rows = this.allRangesFor(practice);
    const labels = practice === null ? undefined : this.categoryLabelsByPractice.get(normalise(practice));
    if (!labels || labels.size === 0) {
      return rows;
    }
    const known = new Set<string>();
    for (const bucket of this.categoryLabelsByPractice.values()) {
      bucket.forEach((label) => known.add(label));
    }
    return rows.filter((row) => {
      const label = normalise(row.categoryShortLabel);
      return labels.has(label) || !known.has(label);
    });
  }

  /**
   * Groups the short labels of every category by the practice of its career path.
   * Categories are grouped by career path in the backend, so the same short label
   * comes back once per career path and division; only the distinct labels of a
   * practice are offered, otherwise picking one would select every duplicate.
   */
  private groupLabelsByPractice(
    categories: { categoryMappingsId?: { categoryShortLabel?: string | null }; careerPath?: { practice?: string | null } | null }[],
  ): void {
    const byPractice = new Map<string, Set<string>>();
    for (const category of categories ?? []) {
      const label = normalise(category?.categoryMappingsId?.categoryShortLabel);
      const practice = normalise(category?.careerPath?.practice);
      if (!label || !practice) {
        continue;
      }
      const labels = byPractice.get(practice) ?? new Set<string>();
      labels.add(label);
      byPractice.set(practice, labels);
    }
    this.categoryLabelsByPractice = byPractice;
  }

  /** Creating a range means picking a category of the selected practice that has no range yet. */
  onAdd(): void {
    const availableCategoriesByPractice: { [practice: string]: string[] } = {};
    for (const practice of this.practices) {
      availableCategoriesByPractice[practice] = this.availableFor(practice);
    }

    const ref = this.dialogService.open(EvaluationSalaryRangeFormComponent, {
      header: this.translations['layout.configurations.evaluation.salaryRanges.addRangeDialog.title'],
      width: '480px',
      style: { 'max-width': '95vw' },
      contentStyle: { overflow: 'auto' },
      dismissableMask: true,
      appendTo: 'body',
      data: {
        practices: this.practices,
        availableCategoriesByPractice,
        practice: this.selectedPractice,
      },
    });

    ref.onClose.subscribe((result) => {
      if (!result) return;
      this.getAll();
      this.messageService.add({
        severity: 'success',
        summary: this.translations['layout.configurations.evaluation.salaryRanges.addRangeDialog.successMessage.title'],
        detail: this.translations['layout.configurations.evaluation.salaryRanges.addRangeDialog.successMessage.detail'],
      });
    });
  }

  onDelete(event: Event, range: EvaluationCategoryGroupSalaryRange): void {
    if (!range.id) return;

    this.confirmationService.confirm({
      key: 'salaryRanges',
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.detail'],
      icon: 'pi pi-exclamation-triangle',
      header: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.delete'],
      rejectLabel: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.cancel'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteRange(range.id as number),
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.cancelDeletion.title'],
          detail: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.cancelDeletion.detail'],
          life: 3000,
        });
      },
    });
  }

  private deleteRange(id: number): void {
    this.evaluationCategoryGroupSalaryRangeService.delete(id).subscribe({
      next: () => {
        this.getAll();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.successMessage.title'],
          detail: this.translations['layout.configurations.evaluation.salaryRanges.deleteRange.successMessage.detail'],
        });
      },
      error: (error) => {
        console.log('Error deleting the salary range: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.title'],
          detail: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.detail'],
        });
      },
    });
  }

  save(): void {
    if (!this.canSave || this.selectedPractice === null) {
      return;
    }
    this.saving = true;
    this.evaluationCategoryGroupSalaryRangeService
      .replaceForPractice(this.selectedPractice, this.copyRanges(this.ranges))
      .subscribe({
        next: (saved) => {
          this.allRanges = this.allRanges.filter((range) => normalise(range.practice) !== normalise(this.selectedPractice!));
          this.allRanges = this.allRanges.concat(saved);
          this.applySelectedPractice();
          this.messageService.add({
            severity: 'success',
            summary: this.translations['layout.configurations.evaluation.salaryRanges.successMessage.title'],
            detail: this.translations['layout.configurations.evaluation.salaryRanges.saved'],
          });
          this.saving = false;
        },
        error: (error) => {
          console.log('Error saving the salary ranges: ', error);
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.title'],
            detail: this.translations['layout.configurations.evaluation.salaryRanges.errorMessage.detail'],
          });
        },
      });
  }

  cancelChanges(): void {
    this.applySelectedPractice();
  }

  private copyRanges(ranges: EvaluationCategoryGroupSalaryRange[]): EvaluationCategoryGroupSalaryRange[] {
    return ranges.map((range) => ({
      id: range.id,
      categoryShortLabel: range.categoryShortLabel,
      practice: range.practice,
      lowSalary: range.lowSalary,
      mediumSalary: range.mediumSalary,
      highSalary: range.highSalary,
      exceedSalary: range.exceedSalary,
    }));
  }

  isChanged(rowIndex: number, field: SalaryField): boolean {
    const savedRow = this.savedRows[rowIndex];
    const row = this.ranges[rowIndex];
    return !!savedRow && !!row && Number(savedRow[field] ?? null) !== Number(row[field] ?? null);
  }

  onCellChange(row: EvaluationCategoryGroupSalaryRange, field: SalaryField, value: number | null): void {
    row[field] = value ?? null;
  }

  private values(ranges: EvaluationCategoryGroupSalaryRange[]): unknown[] {
    return ranges.map((range) => [
      range.categoryShortLabel,
      range.lowSalary,
      range.mediumSalary,
      range.highSalary,
      range.exceedSalary,
    ]);
  }

  private readonly numberPipe = new DecimalPipe('en-US');
}